import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Plus, MapPin, Bed, Bath, Trash2, ShieldCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";

const PLACEHOLDER = "https://images.pexels.com/photos/31656143/pexels-photo-31656143.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const STATUS_OPTIONS = ["Available", "Rented", "Reserved", "Other"];
const STATUS_COLORS = {
  Available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rented:    "bg-blue-50 text-blue-700 border-blue-200",
  Reserved:  "bg-amber-50 text-amber-700 border-amber-200",
  Other:     "bg-slate-100 text-slate-700 border-slate-300",
};

const emptyForm = {
  name: "", address: "", city: "", state: "", zip_code: "",
  bedrooms: 0, bathrooms: 0, monthly_rent: 0, image_url: "",
  status: "Available", status_other: "",
  insurance_provider: "", insurance_policy_number: "", insurance_expiry: "",
  mortgage_lender: "", mortgage_balance: 0, notes: ""
};

export default function PropertiesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "landlord";

  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/properties").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const handleField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...emptyForm,
      ...p,
      status: STATUS_OPTIONS.includes(p.status) ? p.status : "Available",
      status_other: p.status_other || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.status === "Other" && !form.status_other.trim()) {
      toast.error("Please describe the property status in the text box");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        monthly_rent: Number(form.monthly_rent),
        mortgage_balance: Number(form.mortgage_balance),
        status_other: form.status === "Other" ? form.status_other.trim() : "",
      };
      if (editing) {
        await api.put(`/properties/${editing.id}`, body);
        toast.success("Property updated");
      } else {
        await api.post("/properties", body);
        toast.success("Property added");
      }
      setOpen(false); setForm(emptyForm); setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/properties/${confirmDelete.id}`);
      toast.success("Property deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <AppLayout
      title="Properties"
      subtitle="Houses, units & their insurance/mortgage details"
      actions={
        <Button data-testid="add-property-button" onClick={openCreate} className="bg-ink text-ivory hover:bg-gold hover:text-ink rounded-none h-11 px-5 uppercase tracking-[0.2em] text-xs font-semibold gap-2 transition-colors duration-300">
          <Plus className="h-4 w-4" /> Add Property
        </Button>
      }
    >
      {list.length === 0 ? (
        <EmptyState
          kind="properties"
          eyebrow="The Portfolio"
          title="No estates on record yet."
          body="Add your first property to begin tracking units, leases, insurance and mortgage details — all in one curated workspace."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
          {list.map(p => {
            const status = p.status || "Available";
            const statusLabel = status === "Other" ? (p.status_other || "Other") : status;
            const statusClass = STATUS_COLORS[status] || STATUS_COLORS.Other;
            return (
              <Card key={p.id} data-testid={`property-card-${p.id}`} className="rounded-none overflow-hidden border border-bone hover:border-gold transition-colors duration-300">
                <div className="aspect-video bg-slate-100 overflow-hidden relative">
                  <img src={p.image_url || PLACEHOLDER} alt={p.name} className="w-full h-full object-cover" />
                  <span data-testid={`property-status-${p.id}`} className={`absolute top-3 left-3 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border ${statusClass}`}>
                    {statusLabel}
                  </span>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display font-semibold leading-tight">{p.name}</h3>
                    {isAdmin && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => openEdit(p)} data-testid={`edit-property-${p.id}`} className="text-slate-400 hover:text-gold transition-colors p-1">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => setConfirmDelete(p)} data-testid={`delete-property-${p.id}`} className="text-slate-400 hover:text-rose-600 transition-colors p-1">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <MapPin className="h-3.5 w-3.5" />
                    <span className="truncate">{p.address}{p.city ? `, ${p.city}` : ""}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-600">
                    <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" /> {p.bedrooms} bed</span>
                    <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" /> {p.bathrooms} bath</span>
                    <span className="ml-auto font-mono-num font-medium">${p.monthly_rent}/mo</span>
                  </div>
                  {p.insurance_provider && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1.5">
                      <ShieldCheck className="h-3.5 w-3.5" /> Insured · {p.insurance_provider}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="property-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit property" : "Add a property"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4 pt-2">
            <Pair label="Property name" col="col-span-2"><Input data-testid="prop-name" required value={form.name} onChange={e => handleField("name", e.target.value)} /></Pair>
            <Pair label="Address" col="col-span-2"><Input data-testid="prop-address" required value={form.address} onChange={e => handleField("address", e.target.value)} /></Pair>
            <Pair label="City"><Input value={form.city} onChange={e => handleField("city", e.target.value)} /></Pair>
            <Pair label="State"><Input value={form.state} onChange={e => handleField("state", e.target.value)} /></Pair>
            <Pair label="Zip"><Input value={form.zip_code} onChange={e => handleField("zip_code", e.target.value)} /></Pair>
            <Pair label="Monthly rent ($)"><Input type="number" value={form.monthly_rent} onChange={e => handleField("monthly_rent", e.target.value)} /></Pair>
            <Pair label="Bedrooms"><Input type="number" value={form.bedrooms} onChange={e => handleField("bedrooms", e.target.value)} /></Pair>
            <Pair label="Bathrooms"><Input type="number" step="0.5" value={form.bathrooms} onChange={e => handleField("bathrooms", e.target.value)} /></Pair>

            <Pair label="Property status *">
              <Select value={form.status} onValueChange={(v) => handleField("status", v)}>
                <SelectTrigger data-testid="prop-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Pair>
            {form.status === "Other" ? (
              <Pair label="Describe status *">
                <Input
                  data-testid="prop-status-other"
                  required
                  value={form.status_other}
                  onChange={e => handleField("status_other", e.target.value)}
                  placeholder="e.g. Under Renovation"
                />
              </Pair>
            ) : <div />}

            <Pair label="Image URL" col="col-span-2"><Input placeholder="https://…" value={form.image_url} onChange={e => handleField("image_url", e.target.value)} /></Pair>
            <div className="col-span-2 pt-2 micro-label text-gold">Insurance</div>
            <Pair label="Provider"><Input value={form.insurance_provider} onChange={e => handleField("insurance_provider", e.target.value)} /></Pair>
            <Pair label="Policy number"><Input value={form.insurance_policy_number} onChange={e => handleField("insurance_policy_number", e.target.value)} /></Pair>
            <Pair label="Expiry date"><Input type="date" value={form.insurance_expiry} onChange={e => handleField("insurance_expiry", e.target.value)} /></Pair>
            <div className="col-span-2 pt-2 micro-label text-gold">Mortgage</div>
            <Pair label="Lender"><Input value={form.mortgage_lender} onChange={e => handleField("mortgage_lender", e.target.value)} /></Pair>
            <Pair label="Balance ($)"><Input type="number" value={form.mortgage_balance} onChange={e => handleField("mortgage_balance", e.target.value)} /></Pair>
            <Pair label="Notes" col="col-span-2"><Textarea value={form.notes} onChange={e => handleField("notes", e.target.value)} rows={3} /></Pair>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="prop-submit" type="submit" disabled={saving} className="bg-ink text-ivory hover:bg-gold hover:text-ink rounded-none uppercase tracking-[0.2em] text-xs font-semibold">
                {saving ? "Saving…" : (editing ? "Save changes" : "Save property")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this property?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && <>This will permanently remove <strong>{confirmDelete.name}</strong> and cannot be undone. Existing leases, rents and maintenance records will retain their property reference but will no longer link to a property page.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-property" onClick={remove} className="bg-rose-600 hover:bg-rose-700">Yes, delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function Pair({ label, children, col = "" }) {
  return (
    <div className={`space-y-1.5 ${col}`}>
      <Label className="text-xs font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  );
}
